"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "../ui/Button";
import { Music, MessageCircle, Truck } from "lucide-react";

interface SlideBannerProps {
  user?: {
    nickname: string;
  } | null;
}

export default function SlideBanner({ user }: SlideBannerProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#161a2c] via-[#1f2850] to-[#2c3a7b] text-white">
      {/* 미니멀 배경 장식 */}
      <div className="absolute -top-32 -left-24 w-72 h-72 bg-blue-500/15 blur-3xl rounded-full" />
      <div className="absolute bottom-0 right-[-10rem] w-[28rem] h-[28rem] bg-indigo-500/20 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,0.9fr] gap-12 lg:gap-16 items-center">
          {/* 왼쪽: 콘텐츠 영역 */}
          <div className="order-2 lg:order-1 space-y-6 lg:space-y-8">
            {/* 메인 헤드라인 - 최대 2줄 */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-[-0.02em]">
                <span className="block bg-gradient-to-r from-white via-blue-100 to-indigo-100 bg-clip-text text-transparent">
                  모든 악기,
                </span>
                <span className="block bg-gradient-to-r from-white via-blue-100 to-indigo-100 bg-clip-text text-transparent">
                  전국 어디서나.
                </span>
              </h1>

              {/* 서브 문구 - 1줄 */}
              <p className="text-base sm:text-lg lg:text-xl text-white font-medium tracking-tight">
                전국 택배 · 근처 직거래 · 바로 채팅
              </p>
            </div>

            {/* CTA 버튼 - 헤드라인 바로 아래 */}
            <div className="pt-2">
              <Link href="/list" className="inline-block group">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-primary-500 to-primary-400 text-white hover:from-primary-600 hover:to-primary-500 font-semibold px-6 py-3.5 sm:px-8 sm:py-4 text-base sm:text-lg shadow-brand hover:shadow-brandHover transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] rounded-xl"
                >
                  악기 보러가기
                  <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>

            {/* USP - 아이콘 + 단어형 */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
              <USPItem icon={<Music className="w-5 h-5" />} text="전 분야 악기" />
              <USPItem
                icon={<MessageCircle className="w-5 h-5" />}
                text="빠른 채팅"
              />
              <USPItem icon={<Truck className="w-5 h-5" />} text="전국 택배" />
            </div>
          </div>

          {/* 오른쪽: 비주얼 영역 */}
          <div className="order-1 lg:order-2 relative flex justify-center lg:justify-end">
            <div className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-[22rem] lg:h-[22rem]">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-400/30 to-blue-500/20 backdrop-blur-xl border border-white/10" />
              <div className="absolute inset-10 border-2 border-dashed border-white/15 rounded-full animate-[spin_24s_linear_infinite]" />
              <div className="absolute inset-24 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-2xl" />
              <Image
                src="/images/icon.png"
                alt="Tonemarket Instruments"
                fill
                className="object-contain drop-shadow-[0_12px_25px_rgba(0,0,0,0.3)]"
                priority
                sizes="(max-width: 640px) 16rem, (max-width: 1024px) 24rem, 24rem"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface USPItemProps {
  icon: React.ReactNode;
  text: string;
}

function USPItem({ icon, text }: USPItemProps) {
  return (
    <div className="flex items-center gap-2 text-white hover:text-white transition-colors">
      <div className="flex-shrink-0 text-white">{icon}</div>
      <span className="text-sm sm:text-base font-medium tracking-tight">
        {text}
      </span>
    </div>
  );
}
